import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Inference: the half of the course that is about data you do not have.
 *
 * Every technique here is arithmetic on one quantity — σ/√n, the standard error
 * — so the lessons are ordered by how hard it is to fake understanding of them.
 * `sampling-distributions` comes first and is the concept students skip: it is
 * the only one with no procedure to execute, and a student who has not
 * understood *which quantity is varying* can compute intervals correctly for a
 * term and never say what the interval is about.
 *
 * `confidence-intervals` comes before `hypothesis-testing`, which is the
 * reverse of many syllabuses. A test outputs a decision, and a decision is the
 * easiest thing in statistics to produce without understanding: reject, do not
 * reject, done. An interval outputs a range, which cannot be produced without
 * knowing what varies and by how much. In this order the p-value is the
 * interval seen from the other end, rather than a rule about 0.05.
 *
 * `correlation` and `linear-regression` are split where Grade 11 taught them
 * together, because the split is what makes b = r·(s_y/s_x) sayable: the
 * regression slope *is* the correlation, rescaled into the units of the data.
 * That sentence is unavailable to a course where r arrived as "the strength of
 * the fit" in the same breath as the line.
 */
export const INFERENCE_LESSONS: Lesson[] = [
  lesson({
    id: 'sampling-distributions-01',
    unitId: 'unit-inference',
    conceptId: 'sampling-distributions',
    title: 'Sampling Distributions',
    summary: 'The sample mean is itself a random variable — and knowing how much it varies is what makes statistics possible.',
    objectives: [
      'Explain what quantity a sampling distribution describes',
      'Use the standard error σ/√n and say why it shrinks with n',
      'State the central limit theorem and the conditions for using it',
    ],
    estimatedMinutes: 50,
    sections: {
      introduction: {
        title: 'A different 100 people',
        blocks: [
          text('You measure 100 adults and find a mean height of 171.3 cm. Someone else measures a different 100 and finds 169.8 cm. Neither of you made a mistake.'),
          text('The population has one mean, μ, and it is not moving. What moved is the *sample*: a different hundred people, a different average. And that means the thing you computed — the sample mean x̄ — is not a fixed number at all.'),
          insight('**x̄ is a random variable.** It has a distribution, a mean and a standard deviation of its own, exactly like any other random variable in this course. That distribution is called the **sampling distribution of the mean**, and everything in the rest of statistics is a statement about it.'),
          text('This is the hinge of the whole subject, and it is the point at which most students stop following. Up to here, "distribution" described a population — heights, weights, faults. From here on the distribution being talked about is usually a distribution of *sample means*, and the two are different objects with different spreads.'),
        ],
      },
      intuition: {
        title: 'Three facts, and the one that matters',
        blocks: [
          text('Take samples of size n from a population with mean μ and standard deviation σ. The sampling distribution of x̄ has:'),
          math('E(\\bar{x}) = \\mu, \\qquad \\operatorname{SD}(\\bar{x}) = \\frac{\\sigma}{\\sqrt{n}}'),
          list([
            '**Centred correctly.** The sample mean is right on average — it does not systematically over- or under-shoot. A statistic with this property is called *unbiased*.',
            '**Narrower than the population, by a factor of √n.** This is the **standard error**, and it is the single most important quantity in inference.',
            '**Approximately normal for large n**, whatever the population looked like. That is the central limit theorem.',
          ]),
          insight('The √n is not a convention — it is the variance rule from the random-variables unit, cashed in. x̄ is (X₁ + ⋯ + Xₙ)/n; the variance of the sum is nσ² because the observations are independent, and dividing by n divides the variance by n², leaving σ²/n. Take the square root and there is the standard error. The reason a bigger sample is better is the same reason a difference is noisier than either term.'),
          text('The **central limit theorem** is the surprising part, and it is worth stating carefully:'),
          math('\\bar{x} \\approx N\\!\\left(\\mu, \\frac{\\sigma}{\\sqrt{n}}\\right) \\text{ for large } n, \\text{ whatever the population distribution}'),
          text('Skewed populations, bimodal populations, the flat roll of a die — average enough of them and the averages are normal. Roughly n ≥ 30 suffices unless the population is severely skewed; if the population is *already* normal, x̄ is exactly normal for every n, including n = 1.'),
          warning('The central limit theorem says nothing about the population becoming normal. Incomes are skewed and stay skewed however many people you survey. It is the distribution of the *sample means* that straightens out, and confusing the two is the standard misreading.', 'The population does not change'),
          warning('√n means diminishing returns, and they bite fast. Halving the standard error requires four times the data; a tenth of the standard error requires a hundred times. This is why national surveys stop around 1000 people — the next percentage point of precision costs three thousand more interviews.', 'Four times the data, half the error'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'normal-curve',
              mean: 170,
              sd: 10,
              marks: [-2, -1, 0, 1, 2],
              unitLabel: 'height of one adult (cm)',
              label: 'the population: σ = 10',
            },
            'One person at a time: heights spread from about 140 to 200 cm.',
          ),
          figure(
            {
              kind: 'normal-curve',
              mean: 170,
              sd: 2,
              marks: [-2, -1, 0, 1, 2],
              unitLabel: 'mean of 25 adults (cm)',
              label: 'sample means, n = 25: σ/√n = 2',
            },
            'The mean of 25 people at a time. Same centre, one fifth of the spread — a sample mean below 164 is now essentially impossible, though individuals that short are common. Averaging destroys variation, and √25 = 5 says by exactly how much.',
          ),
          text('The same shrinkage as a table, since the numbers make the diminishing return plain:'),
          table(
            ['Sample size n', 'Standard error σ/√n (σ = 10)', 'Halved from previous?'],
            [
              ['1', '10.00', '—'],
              ['4', '5.00', 'yes — 4× the data'],
              ['16', '2.50', 'yes — 4× again'],
              ['64', '1.25', 'yes — 4× again'],
              ['100', '1.00', 'no, only a fifth better than n = 64'],
              ['400', '0.50', 'yes — 4× again'],
            ],
          ),
          tip('Whenever a question about "the mean of n" appears, write σ/√n on the first line. Using σ where σ/√n belongs is the error that quietly wrecks every interval and every test that follows.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Component weights have mean 50 g and standard deviation 6 g. (a) What is the probability that one component weighs over 52 g? (b) What is the probability that the mean of 36 components exceeds 52 g? (c) Why are the answers so different?'),
          steps([
            { label: '(a) One component: use σ itself.', latex: 'z = \\frac{52 - 50}{6} \\approx 0.333' },
            { label: 'Upper tail.', latex: 'P \\approx 1 - \\Phi(0.33) = 1 - 0.6293 = 0.371' },
            { label: '(b) A mean of 36: the standard error replaces σ.', latex: '\\text{SE} = \\frac{6}{\\sqrt{36}} = 1' },
            { label: 'Standardise with the standard error.', latex: 'z = \\frac{52 - 50}{1} = 2' },
            { label: 'Upper tail.', latex: 'P \\approx 1 - \\Phi(2) = 0.0228' },
          ]),
          text('(c) About 37% of individual components are over 52 g, and yet a batch of 36 averages over 52 g only 2% of the time. Both are true, and they are not in tension: for the average to land 2 g high, the heavy components must outvote the light ones across all 36, and with independent components that is a much rarer event than one heavy component.'),
          warning('Note that part (b) needed no assumption about the shape of the component weights. n = 36 is past the rule of thumb, so the central limit theorem supplies the normality that the calculation used. Part (a) *did* need the population to be normal, since it is a statement about one component.', 'Which part needed which assumption'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('sampling-distributions', 4)] },
      independent: { title: 'On your own', blocks: [practice('sampling-distributions', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('sampling-distributions', 3, 'Standard errors, the central limit theorem, and one observation against a mean of n.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A sample mean varies from sample to sample: x̄ is a random variable with its own distribution.',
            'E(x̄) = μ — the sample mean is unbiased.',
            'SD(x̄) = σ/√n, the standard error. It shrinks with the square root, so 4× the data halves it.',
            'The central limit theorem makes x̄ approximately normal for n ≳ 30 whatever the population is.',
            'The population itself does not become normal, ever.',
            'Use σ for one observation and σ/√n for a mean of n. Everything downstream depends on the choice.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'confidence-intervals-01',
    unitId: 'unit-inference',
    conceptId: 'confidence-intervals',
    title: 'Confidence Intervals',
    summary: 'Reporting a range instead of a number — and saying honestly what the confidence is confidence in.',
    objectives: [
      'Construct a confidence interval for a mean and for a proportion',
      'Interpret an interval correctly, and reject the common misinterpretation',
      'Say how the level, the spread and the sample size each change the width',
    ],
    estimatedMinutes: 50,
    sections: {
      introduction: {
        title: 'A number with no error bar is a guess',
        blocks: [
          text('Your sample of 100 adults had a mean height of 171.3 cm. Reporting "the population mean is 171.3 cm" is false — the next sample would say something else, and you know it.'),
          text('The previous lesson said exactly how much else: the standard error is σ/√n, and a normal sampling distribution puts 95% of sample means within 1.96 standard errors of μ. Turn that around and 95% of samples produce an x̄ within 1.96 standard errors of μ — so an interval built that way around x̄ captures μ 95% of the time.'),
          math('\\bar{x} \\pm z^{*} \\frac{\\sigma}{\\sqrt{n}}'),
          insight('That is a **confidence interval**: an estimate with its own uncertainty attached. The half-width is the **margin of error**, and it is the part a headline usually omits and a statistician never does.'),
        ],
      },
      intuition: {
        title: 'The three inputs, and the sentence you may not say',
        blocks: [
          text('Every interval in this course has the same shape — *estimate ± critical value × standard error* — and three things go into it:'),
          table(
            ['Input', 'Symbol', 'Effect on width'],
            [
              ['Confidence level', 'z*', 'higher level ⇒ wider'],
              ['Population spread', 'σ', 'more spread ⇒ wider'],
              ['Sample size', 'n', 'more data ⇒ narrower, as 1/√n'],
            ],
          ),
          text('The critical values are worth knowing by sight, because they are the same three numbers forever:'),
          table(
            ['Level', 'z*', 'Where it comes from'],
            [
              ['90%', '1.645', '5% in each tail'],
              ['95%', '1.960', '2.5% in each tail'],
              ['99%', '2.576', '0.5% in each tail'],
            ],
          ),
          text('For a **proportion**, the same shape with the standard error a proportion has:'),
          math('\\hat{p} \\pm z^{*}\\sqrt{\\frac{\\hat{p}(1 - \\hat{p})}{n}}'),
          insight('That square root is the binomial standard deviation √(np(1−p)) divided by n, which is what turns a count into a proportion. The structure is identical; only the standard error is a different formula.'),
          text('And now the interpretation, which is the whole reason the topic is taught before hypothesis testing.'),
          warning('An interval of (169.4, 173.2) does **not** mean "there is a 95% probability that μ is between 169.4 and 173.2". μ is a fixed number. It either is in that interval or it is not, and no probability attaches to a fact. The 95% belongs to the *procedure*: repeat the sampling many times and 95% of the intervals so constructed contain μ. This one either does or does not, and you will never know which.', 'The confidence is in the method'),
          text('The acceptable phrasing is short: **"we are 95% confident that μ lies between 169.4 and 173.2"** — where "95% confident" is a claim about the reliability of the procedure that produced the numbers.'),
          insight('A second thing an interval reports for free: any value inside it is *plausible* given the data, and any value outside it is not — at that level. That observation is the whole of the next lesson, which is why hypothesis testing has so little new machinery in it.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'normal-curve',
              mean: 0,
              sd: 1,
              shade: { from: -1.96, to: 1.96, label: '95%' },
              marks: [-1.96, -1, 0, 1, 1.96],
              label: 'where z* = 1.96 comes from',
            },
            'The middle 95% of the standard normal runs from −1.96 to +1.96, leaving 2.5% in each tail. Widening to 99% needs 2.576, and narrowing to 90% needs only 1.645 — the level is bought with width.',
          ),
          text('Twenty samples from a population with μ = 170, each giving a 95% interval:'),
          table(
            ['Sample', 'x̄', '95% interval', 'Contains μ = 170?'],
            [
              ['1', '169.1', '(167.2, 171.1)', 'yes'],
              ['2', '171.8', '(169.8, 173.8)', 'yes'],
              ['3', '172.4', '(170.4, 174.4)', '**no**'],
              ['4', '170.2', '(168.2, 172.2)', 'yes'],
              ['…', '…', '…', '…'],
              ['**Overall**', '', '', '**19 of 20**'],
            ],
          ),
          text('Sample 3 is not a mistake and not bad luck to be avoided. One interval in twenty is expected to miss — that is precisely what "95%" purchased. What is not knowable is which one, which is exactly why the probability statement about a single interval is forbidden.'),
          tip('State the level, the interval, and the parameter in one sentence: "we are 95% confident the mean adult height is between 169.4 cm and 173.2 cm". If the sentence has no parameter in it, the interval has not been interpreted.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('(a) A sample of 64 components has mean 49.2 g; the population standard deviation is known to be 6 g. Find a 95% confidence interval for the population mean. (b) In a poll of 400 people, 168 support a measure. Find a 99% interval for the true proportion. (c) How large a sample would part (a) need for a margin of error of 0.5 g?'),
          steps([
            { label: '(a) Standard error first.', latex: '\\text{SE} = \\frac{6}{\\sqrt{64}} = 0.75' },
            { label: 'Margin of error at 95%.', latex: '1.96 \\times 0.75 = 1.47' },
            { label: 'Interval.', latex: '49.2 \\pm 1.47 = (47.73,\\; 50.67)\\text{ g}' },
            { label: '(b) The sample proportion, then its standard error.', latex: '\\hat{p} = \\tfrac{168}{400} = 0.42, \\quad \\text{SE} = \\sqrt{\\tfrac{0.42 \\times 0.58}{400}} \\approx 0.0247' },
            { label: 'At 99%, z* = 2.576.', latex: '0.42 \\pm 2.576(0.0247) = 0.42 \\pm 0.0636' },
            { label: 'Interval.', latex: '(0.356,\\; 0.484) \\text{ — about } 36\\% \\text{ to } 48\\%' },
            { label: '(c) Set the margin of error and solve for n.', latex: '1.96 \\times \\frac{6}{\\sqrt{n}} \\le 0.5 \\Rightarrow \\sqrt{n} \\ge 23.52' },
            { label: 'Square, and round **up** — a fractional component is not a sample.', latex: 'n \\ge 553.2 \\Rightarrow n = 554' },
          ]),
          text('Part (b) is worth a comment beyond the arithmetic. The interval (0.356, 0.484) sits entirely below 0.5, so the data is consistent with the measure being unpopular and not consistent with it having majority support — at the 99% level. That is a conclusion, drawn from an interval, without any test having been performed.'),
          warning('In step 8 the rounding must be upwards even when the decimal is small: n = 553 gives a margin of error just over 0.5 and fails the requirement. Sample-size answers always round up.', 'Round up, always'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('confidence-intervals', 4)] },
      independent: { title: 'On your own', blocks: [practice('confidence-intervals', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('confidence-intervals', 3, 'Proportions, required sample sizes, and interpretations that are and are not allowed.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Every interval is estimate ± critical value × standard error.',
            'For a mean: x̄ ± z*σ/√n. For a proportion: p̂ ± z*√(p̂(1 − p̂)/n).',
            'z* is 1.645, 1.960, 2.576 for 90%, 95% and 99%.',
            'The confidence describes the procedure, not the one interval you computed.',
            'Higher confidence widens; a larger sample narrows, as 1/√n.',
            'A required sample size is solved from the margin-of-error formula and rounded up.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'hypothesis-testing-01',
    unitId: 'unit-inference',
    conceptId: 'hypothesis-testing',
    title: 'Hypothesis Testing',
    summary: 'Asking whether the data would be surprising if nothing were going on — and what a small p-value does not say.',
    objectives: [
      'State null and alternative hypotheses for a claim',
      'Compute a test statistic and a p-value, and reach a decision',
      'Interpret a p-value correctly and distinguish significance from importance',
    ],
    estimatedMinutes: 50,
    sections: {
      introduction: {
        title: 'Would this be surprising if nothing were happening?',
        blocks: [
          text('A machine is supposed to fill jars to 500 g. A sample of 36 jars averages 497.8 g. Is the machine broken, or is that just the wobble the last two lessons described?'),
          text('The sampling distribution answers it. If the machine is fine, x̄ has mean 500 and standard error σ/√n; the question is whether 497.8 is a plausible draw from that distribution or an implausible one.'),
          insight('That is the entire logic of a **hypothesis test**: assume nothing is going on, work out how surprising the data would then be, and reject the assumption only if the data is surprising enough. It is proof by contradiction with the contradiction softened into improbability.'),
          text('You already have the machinery. A 95% confidence interval for μ would be (494.8, 500.8), which contains 500 — so 500 is a plausible value and there is no case to answer. A test is that same comparison run from the other end, with a number attached to how close the call was.'),
        ],
      },
      intuition: {
        title: 'The five steps, and the four ways to misread the answer',
        blocks: [
          text('Every test in this course follows the same five steps:'),
          list(
            [
              '**Hypotheses.** H₀ is the "nothing is going on" claim, always an equality: μ = 500. H₁ is what you would conclude instead — μ ≠ 500 (two-tailed), or μ < 500 / μ > 500 (one-tailed).',
              '**Significance level.** α, chosen *before* looking at the data. Usually 0.05.',
              '**Test statistic.** How many standard errors the data sits from the null value: z = (x̄ − μ₀)/(σ/√n).',
              '**p-value.** The probability of a result at least this extreme *if H₀ were true*.',
              '**Decide, in context.** p < α ⇒ reject H₀; otherwise do not reject. Then say what that means about jars.',
            ],
            true,
          ),
          warning('A two-tailed test doubles the tail area, because "at least this extreme" includes being extreme in the other direction. Choose the tails from the question — before seeing which way the data went. Picking a one-tailed test after noticing the direction halves the p-value for free and is a form of cheating with a name: p-hacking.', 'Choose the tails first'),
          text('The p-value is the part that is misread, so here are the four misreadings, side by side with the truth:'),
          table(
            ['A p-value is NOT…', 'It IS…'],
            [
              ['the probability that H₀ is true', 'the probability of data this extreme *if* H₀ is true'],
              ['the probability the result was chance', 'a statement about the data, conditional on H₀'],
              ['a measure of how big the effect is', 'a measure of how well the data rules out H₀'],
              ['proof of H₀ when it is large', 'a failure to find evidence — absence of evidence, not evidence of absence'],
            ],
          ),
          insight('The first row is Bayes’ theorem again. P(data | H₀) and P(H₀ | data) are different conditional probabilities, and the second one cannot be computed without a prior. Every over-claimed research headline lives in the gap between those two.'),
          text('Two errors are possible, and they trade off against each other:'),
          table(
            ['', 'H₀ actually true', 'H₀ actually false'],
            [
              ['Reject H₀', '**Type I error** — probability α', 'correct'],
              ['Do not reject', 'correct', '**Type II error** — probability β'],
            ],
          ),
          warning('Lowering α to 0.01 makes false alarms rarer and real effects easier to miss. There is no setting that avoids both; α encodes which error is worse in context. For a smoke alarm, a false alarm is cheap and a miss is fatal, and the threshold should say so.', 'You cannot minimise both errors'),
          text('And the distinction that matters most outside the classroom: **statistical significance is not importance.** A large enough sample makes any difference, however tiny, significant. A drug that lowers blood pressure by 0.2 mmHg with p = 0.001 is a real effect and a useless one.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'normal-curve',
              mean: 500,
              sd: 1,
              shade: { from: -3.4, to: -1.96, label: 'α/2' },
              marks: [-2.2, -1.96, 0, 1.96],
              unitLabel: 'sample mean jar weight (g), if H₀ is true',
              label: 'the two-tailed rejection region',
            },
            'The distribution of x̄ assuming the machine is fine. Beyond ±1.96 standard errors lies 5% of the probability, split between the tails; a sample landing there is the evidence that rejects H₀. The observed x̄ sits at z = −2.2, inside the shaded tail.',
          ),
          text('The same decision, three ways — and all three always agree:'),
          table(
            ['Method', 'The comparison', 'Verdict here'],
            [
              ['p-value', 'p = 0.028 against α = 0.05', 'reject H₀'],
              ['critical value', '|z| = 2.2 against z* = 1.96', 'reject H₀'],
              ['confidence interval', 'does (497.15, 498.45) contain 500?', 'no ⇒ reject H₀'],
            ],
          ),
          insight('The third row is the point of teaching intervals first. A two-tailed test at α rejects H₀ exactly when the (1 − α) confidence interval excludes the null value. They are the same statement, and the interval version also tells you what the mean plausibly *is* — which the test never does.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A machine should fill jars to 500 g with σ = 6 g. A sample of 36 jars has mean 497.8 g. Test at the 5% level whether the machine is off target.'),
          steps([
            { label: 'Hypotheses. "Off target" is either direction, so two-tailed.', latex: 'H_0: \\mu = 500 \\quad \\text{vs} \\quad H_1: \\mu \\ne 500' },
            { label: 'Significance level, chosen in advance.', latex: '\\alpha = 0.05' },
            { label: 'Standard error.', latex: '\\text{SE} = \\frac{6}{\\sqrt{36}} = 1' },
            { label: 'Test statistic.', latex: 'z = \\frac{497.8 - 500}{1} = -2.2' },
            { label: 'p-value — one tail, then doubled for two.', latex: 'p = 2 \\times \\Phi(-2.2) = 2(0.0139) = 0.0278' },
            { label: 'Compare and decide.', latex: '0.0278 < 0.05 \\Rightarrow \\text{reject } H_0' },
          ]),
          text('**In context:** there is evidence at the 5% level that the machine is not filling to 500 g on average; the sample suggests it is underfilling by about 2.2 g. That last clause matters — the test alone reports only "not 500", and the *estimate* of how far off is what a maintenance engineer actually needs.'),
          warning('Had the sample mean been 498.2, z would be −1.8 and p = 0.072. The conclusion would be "no evidence at the 5% level that the machine is off target" — which is emphatically not "the machine is fine". A sample of 36 simply cannot detect a drift that small, and the honest report says so.', 'Not rejecting is not accepting'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('hypothesis-testing', 4)] },
      independent: { title: 'On your own', blocks: [practice('hypothesis-testing', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('hypothesis-testing', 3, 'One- and two-tailed tests, Type I and II errors, and p-values interpreted correctly.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'H₀ is the no-effect claim and always carries the equals sign; H₁ is what you would conclude instead.',
            'z = (x̄ − μ₀) ÷ (σ/√n): how many standard errors the data sits from the null value.',
            'A p-value is P(data this extreme | H₀), never P(H₀ | data).',
            'Reject when p < α; failing to reject is not proof of H₀.',
            'Two-tailed doubles the tail area, and the tails are chosen before seeing the data.',
            'Type I is a false alarm (probability α); Type II is a missed effect. Lowering one raises the other.',
            'Significant does not mean large — a big enough sample makes any difference significant.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'correlation-01',
    unitId: 'unit-two-variables',
    conceptId: 'correlation',
    title: 'Correlation',
    summary: 'Measuring linear association as an average of products of z-scores — which is why it has no units and cannot exceed 1.',
    objectives: [
      'Interpret r as an average product of standardised values',
      'Say why r is unit-free and bounded by ±1',
      'Identify the situations where r is misleading despite being correct',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Where does r come from?',
        blocks: [
          text('Grade 11 introduced r as a number between −1 and +1 measuring how tightly points hug a line. True, and it leaves two obvious questions unanswered: why can it not be 1.4, and why does it not change when you switch from centimetres to inches?'),
          text('Both answers are in the definition, which is simpler than its reputation:'),
          math('r = \\frac{1}{n-1}\\sum z_x z_y, \\qquad z_x = \\frac{x - \\bar{x}}{s_x}, \\quad z_y = \\frac{y - \\bar{y}}{s_y}'),
          insight('Standardise both variables, multiply each pair of z-scores, and average. That is all r is: **the average product of standardised values.** Everything strange about it follows from those two words.'),
        ],
      },
      intuition: {
        title: 'Reading the formula',
        blocks: [
          text('**Why it is unit-free.** Standardising divides by the standard deviation, so a z-score has no units — it is a count of standard deviations. Products of unit-free numbers are unit-free, and the average of them is too. Measure height in metres or in furlongs and every z-score is identical, so r is identical.'),
          text('**Why the sign works.** Split the scatter into quadrants at (x̄, ȳ):'),
          table(
            ['Quadrant', 'z_x', 'z_y', 'Product'],
            [
              ['upper right', '+', '+', 'positive'],
              ['lower left', '−', '−', 'positive'],
              ['upper left', '−', '+', 'negative'],
              ['lower right', '+', '−', 'negative'],
            ],
          ),
          text('Points where both variables are above average, or both below, push r up. Points where one is above and the other below push it down. A cloud filling all four quadrants evenly cancels to nothing.'),
          text('**Why |r| ≤ 1.** The products are largest when every z_y equals its z_x exactly — every point on one straight line — and then the sum is a sum of squares that averages to exactly 1. Any wobble off the line puts some products below their maximum, so r falls. It is an average of things that cannot beat 1, so it cannot beat 1.'),
          insight('This also settles the symmetry: swapping x and y swaps the two factors in every product and changes nothing. Correlation has no direction of causation built into it, because it has no direction at all.'),
          text('And now the limits, which are where the marks and the professional errors live:'),
          list([
            '**r measures *linear* association only.** A perfect parabola through the origin can have r = 0. The relationship is exact and the linear correlation is nil.',
            '**r is not resistant.** One outlier can drag r from 0.1 to 0.8, or the reverse. Always look at the scatter plot before quoting the number.',
            '**Sign is direction, size is strength.** r = −0.9 is exactly as strong as +0.9. Ranking coefficients by value rather than by absolute value calls the strongest relationship the weakest.',
            '**Correlation is not causation** — and the reason is worth having: a lurking third variable can produce association with no causal link at all.',
          ]),
          warning('r² has a name and a meaning, and it is not r: the **coefficient of determination**, the proportion of the variation in y that the linear relationship accounts for. r = 0.6 sounds like most of the story and r² = 0.36 says the line explains barely a third of it.', 'r = 0.6 is not 60% of anything'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: 0,
              max: 12,
              points: [
                { x: 1, y: 2 },
                { x: 2, y: 3 },
                { x: 3, y: 5 },
                { x: 4, y: 5 },
                { x: 5, y: 7 },
                { x: 6, y: 8 },
                { x: 7, y: 8 },
                { x: 8, y: 10 },
              ],
              lines: [{ slope: 1.07, intercept: 1.05, dashed: true, label: 'r ≈ 0.98' }],
            },
            'Nearly every point in the upper-right or lower-left quadrant relative to the means, so nearly every product of z-scores is positive and large. r ≈ 0.98.',
          ),
          figure(
            {
              kind: 'coordinate-grid',
              min: 0,
              max: 12,
              points: [
                { x: 1, y: 9 },
                { x: 2, y: 4 },
                { x: 3, y: 7 },
                { x: 4, y: 9 },
                { x: 5, y: 3 },
                { x: 6, y: 8 },
                { x: 7, y: 4 },
                { x: 8, y: 7 },
              ],
              lines: [{ slope: -0.12, intercept: 6.9, dashed: true, label: 'r ≈ −0.10' }],
            },
            'All four quadrants populated, positive and negative products cancelling. r ≈ −0.10 — and the line, which exists regardless, means nothing here.',
          ),
          figure(
            {
              kind: 'coordinate-grid',
              min: -6,
              max: 8,
              points: [
                { x: -4, y: 8 },
                { x: -3, y: 4.5 },
                { x: -2, y: 2 },
                { x: -1, y: 0.5 },
                { x: 0, y: 0 },
                { x: 1, y: 0.5 },
                { x: 2, y: 2 },
                { x: 3, y: 4.5 },
                { x: 4, y: 8 },
              ],
              lines: [{ slope: 0, intercept: 3.3, dashed: true, label: 'r = 0' }],
            },
            'A perfect relationship — y is exactly x²/2 — with r = 0. The left half contributes negative products and the right half positive ones, in exactly equal measure. r is blind to everything that is not a straight line.',
          ),
          tip('Plot first, quote r second, and quote r² third. Each of the three answers a question the next one assumes.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Five students’ study hours x and test scores y have x̄ = 4, s_x = 2, ȳ = 70, s_y = 10, and the products of z-scores total 3.4. Find r and r², interpret both, and say what happens to r if scores are re-marked out of 200 instead of 100.'),
          steps([
            { label: 'Divide the total of the products by n − 1.', latex: 'r = \\frac{3.4}{5 - 1} = 0.85' },
            { label: 'Interpret: sign then size.', latex: 'r = +0.85', note: 'Strong positive linear association — more study, higher scores.' },
            { label: 'Coefficient of determination.', latex: 'r^{2} = 0.7225' },
            { label: 'Interpret r².', latex: '72\\%', note: 'About 72% of the variation in scores is accounted for by the linear relationship with study time. The other 28% is everything else.' },
            { label: 'Doubling every score doubles ȳ and s_y, so every z_y is unchanged.', latex: 'r = 0.85', note: 'Unchanged. A unit change cannot affect a unit-free quantity.' },
          ]),
          text('Step 5 is the property that makes r comparable across studies. Two researchers measuring the same relationship in different units, on different scales, get the same r — which no measure built from raw distances could manage.'),
          warning('And a limit that survives all of this: r = 0.85 says nothing about whether studying *causes* better scores. Students who study more may also attend more lectures and sleep better, and r cannot tell those explanations apart.', 'Still not causation'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('correlation', 4)] },
      independent: { title: 'On your own', blocks: [practice('correlation', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('correlation', 3, 'r from z-scores, r² interpreted, and scatters where r misleads.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'r is the average product of z-scores: standardise both variables, multiply, average over n − 1.',
            'It is unit-free, symmetric in x and y, and confined to [−1, 1].',
            'Sign is direction; absolute size is strength. −0.9 is as strong as +0.9.',
            'r measures *linear* association only — a perfect curve can have r = 0.',
            'One outlier can move r a long way, so plot before quoting.',
            'r² is the proportion of variation in y accounted for by the line, and is much smaller than r.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'linear-regression-01',
    unitId: 'unit-two-variables',
    conceptId: 'linear-regression',
    title: 'Least-Squares Regression',
    summary: 'The line, computed rather than drawn — and its slope turns out to be the correlation wearing the units of the data.',
    objectives: [
      'Compute a least-squares line from summary statistics',
      'Interpret the slope and intercept in context, with units',
      'Use residuals to judge whether a line was the right model',
    ],
    estimatedMinutes: 50,
    sections: {
      introduction: {
        title: 'One line, and where it comes from',
        blocks: [
          text('A scatter plot with a clear trend supports infinitely many lines drawn by eye. Least squares picks one, by a rule: the line minimising the total of the squared vertical distances from the points.'),
          math('\\hat{y} = a + bx, \\qquad \\text{minimising} \\sum (y_i - \\hat{y}_i)^{2}'),
          text('Squaring for the same reason as always — so that overshoots and undershoots cannot cancel, and a badly missed point counts for more than a nearly hit one. And the answer is a formula, not a search:'),
          math('b = r\\,\\frac{s_y}{s_x}, \\qquad a = \\bar{y} - b\\bar{x}'),
          insight('Look at the slope. **The regression slope is the correlation, rescaled into the units of the data.** r says how tightly the variables move together in unit-free terms; multiplying by s_y/s_x converts that into "score points per hour of study". The two topics were never separate.'),
        ],
      },
      intuition: {
        title: 'What the two numbers say, and what the leftovers say',
        blocks: [
          text('**The slope b** is the predicted change in y for a one-unit increase in x, and it carries the units of the data — "6.5 marks per hour", never a bare 6.5. It also inherits the sign of r, since s_y and s_x are both positive.'),
          text('**The intercept a** is the prediction at x = 0. Sometimes that is meaningful; often x = 0 is far outside the data and the intercept is an artefact of extending the line to meet the axis. It is always needed to draw the line and rarely worth interpreting.'),
          insight('The formula a = ȳ − bx̄ rearranges to ȳ = a + bx̄, which says the line passes exactly through (x̄, ȳ) — the point of averages. Every least-squares line does, whatever the data, which makes it a free check on any line you compute.'),
          text('**Residuals** are what is left over, and they are where the real diagnostic work happens:'),
          math('\\text{residual} = y - \\hat{y} = \\text{actual} - \\text{predicted}'),
          list([
            'A positive residual means the point is *above* the line — the actual value exceeded the prediction.',
            'The residuals of a least-squares line always total zero, so their total is no evidence of anything.',
            'Plotting residuals against x is the test of whether a line was the right model at all.',
          ]),
          warning('Actual minus predicted, in that order. Reversing it flips every sign and turns "the model under-predicted this point" into its opposite — a full reversal of the interpretation for an answer that is numerically nearly right.', 'The order of the subtraction is the meaning'),
          insight('A residual plot with no pattern — points scattered evenly above and below zero — says the line captured the structure. A residual plot with a **curve** in it says a straight line was wrong, however good r looked; the leftovers still contain a relationship the model did not use. That check is invisible in r and obvious in the plot.'),
          text('Two limits carry over from Grade 11 and are worth restating in this stronger setting:'),
          list([
            '**Extrapolation.** The line was fitted over the observed range of x, and outside it nothing was measured. Predicting there is not supported, however good the fit inside.',
            '**Causation.** A slope of 6.5 marks per hour does not establish that studying causes marks. Regression describes association with a line; it has no more causal content than r did.',
          ]),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: 0,
              max: 12,
              points: [
                { x: 1, y: 3 },
                { x: 2, y: 4 },
                { x: 3, y: 4 },
                { x: 4, y: 6 },
                { x: 5, y: 6 },
                { x: 6, y: 8 },
                { x: 7, y: 8 },
                { x: 8, y: 9 },
              ],
              lines: [{ slope: 0.9, intercept: 2.1, label: 'ŷ = 2.1 + 0.9x' }],
            },
            'The vertical gaps are the residuals, and the line is the one making the total of their squares as small as any line can. It passes through the point of averages, (4.5, 6).',
          ),
          text('Residuals for the fit above, and the pattern to look for:'),
          table(
            ['x', 'y', 'ŷ', 'residual'],
            [
              ['1', '3', '3.0', '0.0'],
              ['2', '4', '3.9', '+0.1'],
              ['3', '4', '4.8', '−0.8'],
              ['4', '6', '5.7', '+0.3'],
              ['5', '6', '6.6', '−0.6'],
              ['6', '8', '7.5', '+0.5'],
              ['7', '8', '8.4', '−0.4'],
              ['8', '9', '9.3', '−0.3'],
              ['**Total**', '', '', '**≈ 0**'],
            ],
          ),
          text('Signs alternating, no drift, no curve: a straight line was the right choice here. Compare with the case where it was not:'),
          figure(
            {
              kind: 'coordinate-grid',
              min: 0,
              max: 12,
              points: [
                { x: 1, y: 2 },
                { x: 2, y: 4.5 },
                { x: 3, y: 6.5 },
                { x: 4, y: 8 },
                { x: 5, y: 9 },
                { x: 6, y: 9.5 },
                { x: 7, y: 9.8 },
                { x: 8, y: 10 },
              ],
              lines: [{ slope: 1.1, intercept: 2.6, label: 'a good r, a bad model' }],
            },
            'The points curve and the line does not. r here is about 0.94 — comfortably "strong" — and yet the line is above the data at both ends and below it in the middle, which a residual plot would show as an unmistakable arch. A high r never certifies that a line was the right shape.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Study hours and test scores for 20 students give x̄ = 4, s_x = 2, ȳ = 70, s_y = 10, r = 0.85, over a range of 1 to 8 hours. Find the least-squares line, interpret both coefficients, predict the score for 5 hours, find the residual for a student who studied 5 hours and scored 80, and comment on predicting for 20 hours.'),
          steps([
            { label: 'Slope: the correlation, rescaled.', latex: 'b = 0.85 \\times \\frac{10}{2} = 4.25' },
            { label: 'Intercept: force the line through the point of averages.', latex: 'a = 70 - 4.25(4) = 53' },
            { label: 'The line.', latex: '\\hat{y} = 53 + 4.25x' },
            { label: 'Interpret, with units.', latex: 'b = 4.25', note: 'Each extra hour of study is associated with about 4.25 more marks.' },
            { label: 'Predict at x = 5.', latex: '\\hat{y} = 53 + 4.25(5) = 74.25' },
            { label: 'Residual for the student who scored 80.', latex: '80 - 74.25 = +5.75', note: 'Above the line — they did better than the model predicted.' },
            { label: 'Now x = 20, well outside 1 to 8 hours.', latex: '\\hat{y} = 53 + 4.25(20) = 138' },
          ]),
          text('A score of 138 out of 100 is the arithmetic answering a question the data cannot. Nobody in the study worked 20 hours, so the line carries no evidence about that region — and the impossible figure is a mercy, since an extrapolation landing on a plausible-looking number is equally unfounded and far harder to spot.'),
          warning('The intercept of 53 is the predicted score for a student who studies zero hours. That is just outside the observed range of 1 to 8, so it is a mild extrapolation and should be quoted with care rather than as a finding.', 'Even the intercept can be an extrapolation'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('linear-regression', 4)] },
      independent: { title: 'On your own', blocks: [practice('linear-regression', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('linear-regression', 3, 'Lines from summary statistics, residuals, and predictions the data cannot support.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The least-squares line minimises the total of the squared vertical residuals, and there is exactly one.',
            'b = r·(s_y/s_x) — the slope is the correlation converted into the units of the data.',
            'a = ȳ − bx̄, which forces the line through the point of averages (x̄, ȳ).',
            'Interpret the slope with units: "so many y per one x".',
            'A residual is actual − predicted; positive means the point is above the line.',
            'A curved residual plot means a line was the wrong model, whatever r said.',
            'Outside the observed range of x, the line predicts nothing.',
          ]),
        ],
      },
    },
  }),
];
